import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from "../../Api/Api.json";

import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryBar,
} from 'victory';

// Icons for sleep metrics
const SleepDurationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const SleepScoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);

const SleepStartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10,8 16,12 10,16 10,8"></polygon>
  </svg>
);

const METRIC_GOALS = {
  duration: '8.0 hours',
  deep_duration: '2.0 hours',
  rem_duration: '2.0 hours',
  light_duration: '4.0 hours',
  score: '90 points',
  start: '10:00 PM',
};

const getValidYValues = (dataList) => {
  if (!dataList?.length) return [];
  return dataList
    .filter((item) => item?.y != null && !Number.isNaN(Number(item.y)))
    .map((item) => Number(item.y));
};

const getLastGraphValue = (dataList) => {
  if (!dataList?.length) return null;
  for (let i = dataList.length - 1; i >= 0; i -= 1) {
    const y = dataList[i]?.y;
    if (y != null && !Number.isNaN(Number(y))) {
      return Number(y);
    }
  }
  return null;
};

const getAverageGraphValue = (dataList) => {
  const values = getValidYValues(dataList);
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const formatDurationValue = (value) =>
  value == null ? 'Not available' : Number(value).toFixed(1);

const formatScoreValue = (value) =>
  value == null ? 'Not available' : String(Math.round(Number(value)));

const formatSleepStartValue = (value) =>
  value == null ? 'Not available' : `${Number(value)}:00`;

const formatSleepStartAverage = (value) => {
  if (value == null) return 'Not available';
  const totalMinutes = Math.round(Number(value) * 60);
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const displayHour = hours24 % 12 || 12;
  return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
};

const Sleep = () => {
  const navigate = useNavigate();
  const [hrvList, setHRVList] = useState([{}]);
  const [sleepDurationList, setSleepDurationList] = useState([{}]);
  const [deepSleepDurationList, setDeepSleepDurationList] = useState([{}]);
  const [remSleepDurationList, setRemSleepDurationList] = useState([{}]);
  const [lightSleepDurationList, setLightSleepDurationList] = useState([{}]);
  const [sleepStartList, setSleepStartList] = useState([{}]);
  const [sleepEndList, setSleepEndList] = useState([{}]);
  const [sleepScoreList, setSleepScoreList] = useState([{}]);
  const [dayNameList, setDayNameList] = useState([]);
  const [status, setStatus] = useState('');

  // Transform data to use numeric x values based on array index
  const transformDataForChart = (dataList) => {
    if (!dataList || dataList.length === 0) return [];
    return dataList.map((item, index) => ({
      x: index + 1,
      y: item.y,
      date: item.date,
      originalX: item.x
    }));
  };

  const transformedSleepDurationList = transformDataForChart(sleepDurationList);
  const transformedDeepSleepDurationList = transformDataForChart(deepSleepDurationList);
  const transformedRemSleepDurationList = transformDataForChart(remSleepDurationList);
  const transformedLightSleepDurationList = transformDataForChart(lightSleepDurationList);
  const transformedSleepScoreList = transformDataForChart(sleepScoreList);
  const transformedSleepStartList = transformDataForChart(sleepStartList);

  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userid = JSON.parse(localStorage.getItem('user_info')).di_id;
      console.log(userid);

      const headers = { headers: { "Content-Type": "application/json" } }
      const url = Api.FITNESS_API;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const requestData = {
        task: 'getSemionWellnessData',
        id: userid,
        timezone: userTimezone,
      };
      console.log('calling page data api');
      axios.post(url, requestData, headers)
        .then(result => {
          if (result.data) {
            console.log(JSON.stringify(result.data.sleepStartList));
            setHRVList(result.data.hrvList);
            setSleepDurationList(result.data.sleepDurationList);
            setDeepSleepDurationList(result.data.deepSleepDurationList);
            setRemSleepDurationList(result.data.remSleepDurationList);
            setLightSleepDurationList(result.data.lightSleepDurationList);
            setSleepStartList(result.data.sleepStartList);
            setSleepEndList(result.data.sleepEndList);
            setDayNameList(result.data.dayNameList);
            setSleepScoreList(result.data.sleepScoreList);
          }
        });
    }
  }, []);

  const getDataListForMetric = (metricId) => {
    switch (metricId) {
      case 'duration':
        return sleepDurationList;
      case 'deep_duration':
        return deepSleepDurationList;
      case 'rem_duration':
        return remSleepDurationList;
      case 'light_duration':
        return lightSleepDurationList;
      case 'score':
        return sleepScoreList;
      case 'start':
        return sleepStartList;
      default:
        return [];
    }
  };

  const getMetricStats = (metricId) => {
    const dataList = getDataListForMetric(metricId);
    const lastValue = getLastGraphValue(dataList);
    const averageValue = getAverageGraphValue(dataList);

    if (metricId === 'score') {
      return {
        currentValue: formatScoreValue(lastValue),
        averageDisplay: averageValue == null ? 'Not available' : `${Math.round(averageValue)} points`,
        unit: 'points',
      };
    }

    if (metricId === 'start') {
      return {
        currentValue: formatSleepStartValue(lastValue),
        averageDisplay: formatSleepStartAverage(averageValue),
        unit: 'time',
      };
    }

    return {
      currentValue: formatDurationValue(lastValue),
      averageDisplay: averageValue == null ? 'Not available' : `${averageValue.toFixed(1)} hours`,
      unit: 'hours',
    };
  };

  const sleepMetrics = useMemo(
    () => [
      {
        id: 'duration',
        name: 'Sleep Duration',
        description: 'Track your daily sleep duration and rest patterns',
        color: 'bg-[#58A6FF]',
        icon: <SleepDurationIcon />,
        ...getMetricStats('duration'),
        goal: METRIC_GOALS.duration,
      },
      {
        id: 'deep_duration',
        name: 'Deep Sleep Duration',
        description: 'Track your daily deep sleep duration',
        color: 'bg-[#58A6FF]',
        icon: <SleepDurationIcon />,
        ...getMetricStats('deep_duration'),
        goal: METRIC_GOALS.deep_duration,
      },
      {
        id: 'rem_duration',
        name: 'REM Sleep Duration',
        description: 'Track your daily REM sleep duration',
        color: 'bg-[#58A6FF]',
        icon: <SleepDurationIcon />,
        ...getMetricStats('rem_duration'),
        goal: METRIC_GOALS.rem_duration,
      },
      {
        id: 'light_duration',
        name: 'Light Sleep Duration',
        description: 'Track your daily light sleep duration',
        color: 'bg-[#58A6FF]',
        icon: <SleepDurationIcon />,
        ...getMetricStats('light_duration'),
        goal: METRIC_GOALS.light_duration,
      },
      {
        id: 'score',
        name: 'Sleep Score',
        description: 'Monitor your sleep quality and recovery metrics',
        color: 'bg-[#58A6FF]',
        icon: <SleepScoreIcon />,
        ...getMetricStats('score'),
        goal: METRIC_GOALS.score,
      },
      {
        id: 'start',
        name: 'Sleep Start',
        description: 'Track when you go to sleep each night',
        color: 'bg-[#58A6FF]',
        icon: <SleepStartIcon />,
        ...getMetricStats('start'),
        goal: METRIC_GOALS.start,
      },
    ],
    [
      sleepDurationList,
      deepSleepDurationList,
      remSleepDurationList,
      lightSleepDurationList,
      sleepScoreList,
      sleepStartList,
    ]
  );

  const getDurationTransformedList = (metricId) => {
    switch (metricId) {
      case 'duration':
        return transformedSleepDurationList;
      case 'deep_duration':
        return transformedDeepSleepDurationList;
      case 'rem_duration':
        return transformedRemSleepDurationList;
      case 'light_duration':
        return transformedLightSleepDurationList;
      default:
        return [];
    }
  };

  const handleCardClick = (metricType) => {
    // Handle card click - could open detailed view or measurement dialog
    console.log(`Clicked on ${metricType} metric`);
  };

  return (
    <div className="min-h-screen pt-14 pb-20">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium text-white">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Sleep Analysis</h1>
          <p className="text-white">Monitor your sleep patterns and quality</p>
        </div>



        {/* Sleep Metrics with Charts */}
        {sleepMetrics.map((metric) => (
          <div key={metric.id} className="mb-8">
            {/* Card */}
            <div
              onClick={() => handleCardClick(metric.id)}
              className="bg-[#161B22] rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group border border-[#30363D]"
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">Sleep Data</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-white text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>

                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-white">{metric.currentValue}</span>
                    {metric.currentValue !== 'Not available' && (
                      <span className="text-white ml-1">{metric.unit}</span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">Today</div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white">Average</span>
                      <div className="font-medium text-white">
                        {metric.averageDisplay}
                      </div>
                    </div>
                    <div>
                      <span className="text-white">Goal</span>
                      <div className="font-medium text-white">
                        {metric.goal}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart placed immediately after its card */}
            {metric.id === 'duration' ||
             metric.id === 'deep_duration' ||
             metric.id === 'rem_duration' ||
             metric.id === 'light_duration' ? (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
                <VictoryChart
                  domain={{ y: [0, 12] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[0, 2, 4, 6, 8, 10, 12]}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={getDurationTransformedList(metric.id).filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#3b82f6' },
                    }}
                  />
                  <VictoryLine
                    data={getDurationTransformedList(metric.id).filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            ) : null}

            {metric.id === 'score' && (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
                <VictoryChart
                  domain={{ y: [50, 100] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[60, 70, 80, 90, 100]}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={transformedSleepScoreList.filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#10b981' },
                    }}
                  />
                  <VictoryLine
                    data={transformedSleepScoreList.filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            )}

            {metric.id === 'start' && (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px] mb-20">
                <VictoryChart
                  domain={{ y: [0, 12] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[0, 2, 4, 6, 8, 10, 12]}
                    tickFormat={(t) => {
                      if (t === 0) return "12:00 AM";
                      if (t === 12) return "12:00 PM";
                      if (t > 12) return `${t - 12}:00 PM`;
                      return `${t}:00 AM`;
                    }}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={transformedSleepStartList.filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#8b5cf6' },
                    }}
                  />
                  <VictoryLine
                    data={transformedSleepStartList.filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            )}
          </div>
        ))}


      </div>
    </div>
  );
};

export default Sleep;